/**
 * Pack size on a purchase order line, browser side.
 *
 * `supabase/functions/_shared/poLinePack.mjs` is the canonical implementation
 * — it is what the vendor EMAIL renders from. This mirror is what the app
 * renders from. The two must answer identically or a store reads one pack on
 * screen and its vendor reads another; `poLinePack.test.js` runs shared
 * fixtures through both files and pins them together.
 *
 * Pure string work: locate the vendor's pack string on a line, format it
 * beside the order unit. It never parses the pack (that is packText.mjs's
 * job alone) and never reaches for a live purchase option to fill a gap.
 *
 * It also renders the line's ORDERING INSTRUCTION, which is not always a price
 * × a quantity. A catch-weight vendor sells one CASE of ribeyes and invoices
 * $16.40 for every pound that arrives, so the line reads
 *
 *   1 case · 4 PC / AVG 60 LB · $16.40/LB · ~60 LB · ~$984.00
 *
 * and never "QTY 1 LB · TOTAL $16.40". Those billing facts are frozen on the
 * line like `unit` and `unit_price`; classification belongs to
 * billingBasis.mjs, and a sent PO must keep reading the way it was sent.
 */

import { formatMoney, formatQuantity, formatUnitCount } from '../../../supabase/functions/_shared/billingBasis.mjs';

const text = (value) => (value == null ? '' : String(value).trim());

const num = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round2 = (value) => Math.round(value * 100) / 100;

/**
 * The pack size frozen on a purchase order line.
 *
 * The `pack_size` column first; then the option snapshot the line was written
 * from, where rows created before the column existed carry it. '' when the
 * line genuinely has none — a legacy line with no purchase option behind it.
 *
 * @param {object} line  a purchase_order_items row, or a camelCase line object
 * @returns {string}
 */
export function resolvePoLinePackSize(line) {
  if (!line || typeof line !== 'object') return '';
  const direct = text(line.pack_size ?? line.packSize);
  if (direct) return direct;
  const snapshot = (line.snapshot && typeof line.snapshot === 'object') ? line.snapshot : {};
  return text(
    snapshot.pack_description
    ?? snapshot.packDescription
    ?? snapshot.pack_size
    ?? snapshot.packSize,
  );
}

/**
 * What the VENDOR calls this line — their own product wording and their SKU.
 *
 * A PO is read by the vendor's order desk, not by our kitchen. Our catalog
 * name is a canonical rename ("Whole Milk"); the vendor's picker is looking
 * for "WHL MLK GAL 4/1" and the SKU printed beside it. Both have been frozen
 * on the line since 20260724140002 (`snapshot.vendor_item_name` /
 * `snapshot.vendor_sku`, guaranteed top-level by the fanout), and until now
 * nothing rendered them.
 *
 * Column-ish fields first, then the snapshot, exactly as resolvePoLinePackSize
 * does. Returns '' for a half we genuinely do not have — a legacy line with no
 * purchase option behind it — because an invented SKU is worse than none.
 *
 * @param {object} line  a purchase_order_items row, or a camelCase line object
 * @returns {{ name: string, sku: string }}
 */
export function resolvePoLineVendorItem(line) {
  if (!line || typeof line !== 'object') return { name: '', sku: '' };
  const snapshot = (line.snapshot && typeof line.snapshot === 'object') ? line.snapshot : {};
  const name = text(
    line.vendor_item_name
    ?? line.vendorItemName
    ?? snapshot.vendor_item_name
    ?? snapshot.vendorItemName
    ?? snapshot.vendor_description
    ?? snapshot.vendorDescription,
  );
  const sku = text(
    line.vendor_sku
    ?? line.vendorSku
    ?? snapshot.vendor_sku
    ?? snapshot.vendorSku,
  );
  return { name, sku };
}

/**
 * Order unit and pack size in one cell, for surfaces with no room for two
 * columns — "case (8 bags × 12 buns)". Either half stands alone; with neither,
 * ''. Never renders an empty bracket.
 *
 * @param {string} unit
 * @param {string} packSize
 * @returns {string}
 */
export function formatPoOrderUnit(unit, packSize) {
  const orderUnit = text(unit);
  const pack = text(packSize);
  if (orderUnit && pack) return `${orderUnit} (${pack})`;
  return orderUnit || pack;
}

/**
 * The line's ordered amount in the vendor's terms — "12 case", not a bare
 * "12". The caller formats the number; this only attaches the unit it was
 * ordered in.
 *
 * @param {string|number} formattedQuantity  already-formatted quantity
 * @param {string} unit
 * @returns {string}
 */
export function formatPoOrderQuantity(formattedQuantity, unit) {
  const qty = text(formattedQuantity);
  const orderUnit = text(unit);
  if (qty && orderUnit) return `${qty} ${orderUnit}`;
  return qty || orderUnit;
}

/* ── The ordering instruction ─────────────────────────────────────────────── */

/**
 * The facts a line states about how it was ordered and billed. Frozen because
 * these strings are column headings, a vendor email's
 * labels and a test's expectations at once — renaming a key silently blanks a
 * column somewhere.
 */
export const PO_LINE_FIELDS = Object.freeze({
  ORDER_UNIT: 'ORDER UNIT',
  QTY: 'QTY',
  PACK: 'PACK',
  PRICE_BASIS: 'PRICE BASIS',
  EST_WEIGHT: 'EST. WEIGHT',
  EST_TOTAL: 'EST. TOTAL',
});

/** Models whose total is settled on a scale. Mirrors billingBasis.ESTIMATED_MODELS. */
const ESTIMATED_PO_MODELS = Object.freeze(['catch_weight', 'random_weight_piece']);

/**
 * The billing facts FROZEN on a line — the columns first, then the option
 * snapshot the line was written from.
 *
 * As with the pack, nothing is fetched from a live purchase option: a sent PO
 * must keep reading the way it was sent, whatever the vendor's catalog does
 * next. A line with no billing facts is a fixed-package line, which is what
 * every line was before these existed.
 *
 * @param {object} line
 * @returns {{ model: string|null, billingUnit: string, billingUnitPrice: number|null,
 *             estimatedQtyPerOrderUnit: number|null, isEstimated: boolean }}
 */
export function resolvePoLineBilling(line) {
  const empty = {
    model: null,
    billingUnit: '',
    billingUnitPrice: null,
    estimatedQtyPerOrderUnit: null,
    isEstimated: false,
  };
  if (!line || typeof line !== 'object') return empty;
  const snapshot = (line.snapshot && typeof line.snapshot === 'object') ? line.snapshot : {};
  const pick = (snake, camel) =>
    line[snake] ?? line[camel] ?? snapshot[snake] ?? snapshot[camel] ?? null;

  const model = text(pick('fulfillment_model', 'fulfillmentModel')) || null;
  const billingUnit = text(pick('billing_unit', 'billingUnit'));
  const billingUnitPrice = num(pick('billing_unit_price', 'billingUnitPrice'));
  const estimatedQtyPerOrderUnit = num(
    pick('estimated_billing_qty_per_order_unit', 'estimatedBillingQtyPerOrderUnit'),
  );

  return {
    model,
    billingUnit,
    billingUnitPrice,
    estimatedQtyPerOrderUnit,
    // An estimated model with a missing number is not an estimate, it is a
    // gap — such a line falls back to the ordinary quantity × unit price
    // rather than inventing half of one.
    isEstimated: ESTIMATED_PO_MODELS.includes(model)
      && billingUnitPrice !== null
      && estimatedQtyPerOrderUnit !== null,
  };
}

/**
 * One PO line as the order/billing fields plus the frozen count-to-delivery
 * context a buyer needs to audit why that vendor quantity was ordered.
 *
 * For a fixed package this is the familiar quantity × unit price. For a
 * catch-weight line the quantity is in CASES, the price basis is per POUND,
 * and the total is an estimate marked with "~":
 *
 *   ORDER UNIT  case
 *   QTY         1 case
 *   PACK        4 PC / AVG 60 LB
 *   PRICE BASIS $16.40/LB
 *   EST. WEIGHT ~60 LB
 *   EST. TOTAL  ~$984.00
 *
 * Never "QTY 1 LB · TOTAL $16.40" — that is a per-pound rate printed as a line
 * total, and it is out by a factor of sixty.
 *
 * @param {object} line  a purchase_order_items row or camelCase line object
 * @param {{ quantity?: number|null }} [opts]  overrides the stored quantity,
 *   for a screen where the buyer is editing it before the line is saved
 * @returns {{ countedNeed: string, orderUnit: string, qty: string, pack: string,
 *   delivers: string, abovePar: string, priceBasis: string, estWeight: string,
 *   estTotal: string, isEstimated: boolean, fields: object }}
 *   Every value is a string; absent facts are '' and never a guess.
 */
export function buildPoLineDisplay(line, { quantity = null } = {}) {
  const row = (line && typeof line === 'object') ? line : {};
  const billing = resolvePoLineBilling(row);
  const pack = resolvePoLinePackSize(row);
  const orderUnit = text(row.unit);
  const qty = quantity !== null && quantity !== undefined
    ? num(quantity)
    : num(row.order_quantity ?? row.orderQuantity);
  const unitPrice = num(row.unit_price ?? row.unitPrice);

  const qtyText = qty !== null ? formatUnitCount(qty, orderUnit) : '';

  // Count-side facts are frozen into the PO snapshot by allocation fan-out.
  // Read those facts; never reinterpret the pack string or reach for today's
  // purchase option. When an editable draft changes quantity, the frozen
  // per-package factor safely scales the delivery without changing meaning.
  const snapshot = (row.snapshot && typeof row.snapshot === 'object') ? row.snapshot : {};
  const pickCountFact = (snake, camel) =>
    row[snake] ?? row[camel] ?? snapshot[snake] ?? snapshot[camel] ?? null;
  const needQty = num(
    pickCountFact('need_quantity', 'needQuantity')
      ?? pickCountFact('raw_need_quantity', 'rawNeedQuantity'),
  );
  const needUnit = text(pickCountFact('need_unit', 'needUnit'));
  const perPackageValue = num(
    pickCountFact('package_quantity_in_need_unit', 'packageQuantityInNeedUnit'),
  );
  const perPackage = perPackageValue !== null && perPackageValue > 0 ? perPackageValue : null;
  const storedQty = num(row.order_quantity ?? row.orderQuantity);
  const quantityChanged = quantity !== null && quantity !== undefined
    && storedQty !== null && qty !== null && Math.abs(qty - storedQty) > 1e-9;
  let receivedQty = perPackage !== null && qty !== null
    ? Math.round(qty * perPackage * 1e6) / 1e6
    : num(pickCountFact('quantity_received', 'quantityReceived'));
  if (quantityChanged && perPackage === null) receivedQty = null;
  let aboveParQty = receivedQty !== null && needQty !== null
    ? Math.round((receivedQty - needQty) * 1e6) / 1e6
    : num(pickCountFact('overage', 'overage'));
  if (quantityChanged && perPackage === null) aboveParQty = null;

  const countedNeed = needQty !== null && needUnit ? formatUnitCount(needQty, needUnit) : '';
  const estimateMark = billing.isEstimated ? '~' : '';
  const delivers = receivedQty !== null && needUnit
    ? `${estimateMark}${formatUnitCount(receivedQty, needUnit)}`
    : '';
  const abovePar = aboveParQty !== null && aboveParQty > 0 && needUnit
    ? `${estimateMark}${formatUnitCount(aboveParQty, needUnit)}`
    : '';

  let priceBasis = '';
  let estWeight = '';
  let total = null;

  if (billing.isEstimated) {
    priceBasis = billing.billingUnit
      ? `${formatMoney(billing.billingUnitPrice)}/${billing.billingUnit}`
      : formatMoney(billing.billingUnitPrice);
    if (qty !== null) {
      const weight = Math.round(qty * billing.estimatedQtyPerOrderUnit * 1e6) / 1e6;
      estWeight = `~${formatQuantity(weight)}${billing.billingUnit ? ` ${billing.billingUnit}` : ''}`;
      total = round2(weight * billing.billingUnitPrice);
    }
  } else if (unitPrice !== null) {
    priceBasis = orderUnit ? `${formatMoney(unitPrice)}/${orderUnit}` : formatMoney(unitPrice);
    if (qty !== null) total = round2(qty * unitPrice);
  }

  // PRICE UNAVAILABLE is a state, not a zero. `purchase_order_items.unit_price`
  // is nullable (migration 20260821120000) precisely so a line nobody could
  // price is distinguishable from a line that is genuinely free, and every
  // surface has to be able to tell the buyer which one it is looking at.
  // An empty object is not an unpriced line — it is no line at all.
  const priceMissing = !billing.isEstimated && unitPrice === null && qty !== null;

  const totalText = total === null
    ? ''
    : `${billing.isEstimated ? '~' : ''}${formatMoney(total)}`;

  return {
    countedNeed,
    orderUnit,
    qty: qtyText,
    pack,
    delivers,
    abovePar,
    priceBasis,
    estWeight,
    estTotal: totalText,
    isEstimated: billing.isEstimated,
    priceMissing,
    fields: Object.freeze({
      [PO_LINE_FIELDS.ORDER_UNIT]: orderUnit,
      [PO_LINE_FIELDS.QTY]: qtyText,
      [PO_LINE_FIELDS.PACK]: pack,
      [PO_LINE_FIELDS.PRICE_BASIS]: priceBasis,
      [PO_LINE_FIELDS.EST_WEIGHT]: estWeight,
      [PO_LINE_FIELDS.EST_TOTAL]: totalText,
    }),
  };
}

/**
 * The whole instruction on one line, for an email body or a narrow screen:
 *
 *   "1 case · 4 PC / AVG 60 LB · $16.40/LB · ~60 LB · ~$984.00"
 *   "12 cases · 8 bags × 12 buns · $118.00/case · $1,416.00"
 *
 * Empty facts drop out rather than leaving an empty separator.
 *
 * @param {object} line
 * @param {{ quantity?: number|null }} [opts]
 * @returns {string}
 */
export function formatPoLineSummary(line, opts = {}) {
  const display = buildPoLineDisplay(line, opts);
  return [display.qty, display.pack, display.priceBasis, display.estWeight, display.estTotal]
    .filter(Boolean)
    .join(' · ');
}
